import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

const shopPackHandles = [
  'share-pack-3-cards-one-design',
  'variety-4-pack-pick-any-4',
  'variety-10-pack-all-10-meme-cards',
];

function isShopAllCollection() {
  return window.location.pathname === '/collections/all' || window.location.pathname === '/collections/all/';
}

function getShopPackCard(handle) {
  const anchor = document.querySelector('li.product-grid__item a[href*="/products/' + handle + '"]') ||
    document.querySelector('a[href*="/products/' + handle + '"]');

  return anchor ? anchor.closest('li.product-grid__item') : null;
}

function moveShopPackProductsLast() {
  if (!isShopAllCollection()) return;

  const cards = shopPackHandles.map(getShopPackCard).filter(Boolean);
  if (!cards.length) return;

  const grid = cards[0].parentElement;
  if (!grid) return;

  const currentTail = Array.from(grid.children).slice(-cards.length);
  const alreadyLast = cards.every((card, index) => currentTail[index] === card);

  cards.forEach((card, index) => {
    card.style.order = String(1000 + index);
  });

  if (alreadyLast) return;

  shopPackHandles.forEach((handle) => {
    const card = getShopPackCard(handle);
    if (card && card.parentElement === grid) {
      grid.appendChild(card);
    }
  });
}

function startShopPackOrderWatcher() {
  if (!isShopAllCollection()) return;

  document.documentElement.dataset.packOrderScript = 'active';
  moveShopPackProductsLast();

  document.addEventListener('shopify:section:load', moveShopPackProductsLast);
  window.addEventListener('load', moveShopPackProductsLast);

  let retries = 0;
  function retryMove() {
    moveShopPackProductsLast();
    retries += 1;
    if (retries < 20) {
      window.setTimeout(retryMove, 300);
    }
  }
  retryMove();

  if ('MutationObserver' in window && document.body) {
    new MutationObserver(moveShopPackProductsLast).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startShopPackOrderWatcher);
} else {
  startShopPackOrderWatcher();
}
